import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { TransformAccuracyInfo } from '../../services/CoordinateSystemManager';

export interface CoordinateTransformState {
  sourceCRS: string;
  targetCRS: string;
  sourceCoordinates: string;
  targetCoordinates: string;
  batchInput: string;
  batchOutput: string;
}

interface CoordinateSystemState {
  /** Current map CRS */
  mapCRS: string;
  /** CRS info dialog open state */
  crsInfoDialogOpen: boolean;
  /** CRS Selector modal open state */
  crsSelectorOpen: boolean;
  /** Selected CRS for info display */
  selectedCRSCode: string | null;
  /** Reprojection dialog open state */
  reprojectionDialogOpen: boolean;
  /** Layer selected for reprojection */
  reprojectionLayerId: string | null;
  /** Target CRS for reprojection */
  reprojectionTargetCRS: string;
  /** Reprojection in progress */
  isReprojecting: boolean;
  /** Coordinate transformation tool open state */
  transformToolOpen: boolean;
  /** Coordinate transformation state */
  transform: CoordinateTransformState;
  /** Transformation accuracy info */
  accuracyInfo: TransformAccuracyInfo | null;
  /** Error message */
  error: string | null;
}

const initialState: CoordinateSystemState = {
  mapCRS: 'EPSG:3857',
  crsInfoDialogOpen: false,
  crsSelectorOpen: false,
  selectedCRSCode: null,
  reprojectionDialogOpen: false,
  reprojectionLayerId: null,
  reprojectionTargetCRS: 'EPSG:4326',
  isReprojecting: false,
  transformToolOpen: false,
  transform: {
    sourceCRS: 'EPSG:4326',
    targetCRS: 'EPSG:3857',
    sourceCoordinates: '',
    targetCoordinates: '',
    batchInput: '',
    batchOutput: '',
  },
  accuracyInfo: null,
  error: null,
};

const coordinateSystemSlice = createSlice({
  name: 'coordinateSystem',
  initialState,
  reducers: {
    setMapCRS: (state, action: PayloadAction<string>) => {
      state.mapCRS = action.payload;
    },
    openCRSSelector: (state) => {
      state.crsSelectorOpen = true;
    },
    closeCRSSelector: (state) => {
      state.crsSelectorOpen = false;
    },
    openCRSInfoDialog: (state, action: PayloadAction<string>) => {
      state.crsInfoDialogOpen = true;
      state.selectedCRSCode = action.payload;
    },
    closeCRSInfoDialog: (state) => {
      state.crsInfoDialogOpen = false;
      state.selectedCRSCode = null;
    },
    openReprojectionDialog: (state, action: PayloadAction<string>) => {
      state.reprojectionDialogOpen = true;
      state.reprojectionLayerId = action.payload;
    },
    closeReprojectionDialog: (state) => {
      state.reprojectionDialogOpen = false;
      state.reprojectionLayerId = null;
    },
    setReprojectionTargetCRS: (state, action: PayloadAction<string>) => {
      state.reprojectionTargetCRS = action.payload;
    },
    setIsReprojecting: (state, action: PayloadAction<boolean>) => {
      state.isReprojecting = action.payload;
    },
    openTransformTool: (state) => {
      state.transformToolOpen = true;
    },
    closeTransformTool: (state) => {
      state.transformToolOpen = false;
    },
    setTransformSourceCRS: (state, action: PayloadAction<string>) => {
      state.transform.sourceCRS = action.payload;
    },
    setTransformTargetCRS: (state, action: PayloadAction<string>) => {
      state.transform.targetCRS = action.payload;
    },
    setTransformSourceCoordinates: (state, action: PayloadAction<string>) => {
      state.transform.sourceCoordinates = action.payload;
    },
    setTransformTargetCoordinates: (state, action: PayloadAction<string>) => {
      state.transform.targetCoordinates = action.payload;
    },
    setTransformBatchInput: (state, action: PayloadAction<string>) => {
      state.transform.batchInput = action.payload;
    },
    setTransformBatchOutput: (state, action: PayloadAction<string>) => {
      state.transform.batchOutput = action.payload;
    },
    setAccuracyInfo: (state, action: PayloadAction<TransformAccuracyInfo | null>) => {
      state.accuracyInfo = action.payload;
    },
    setCoordinateSystemError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearCoordinateSystemError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setMapCRS,
  openCRSSelector,
  closeCRSSelector,
  openCRSInfoDialog,
  closeCRSInfoDialog,
  openReprojectionDialog,
  closeReprojectionDialog,
  setReprojectionTargetCRS,
  setIsReprojecting,
  openTransformTool,
  closeTransformTool,
  setTransformSourceCRS,
  setTransformTargetCRS,
  setTransformSourceCoordinates,
  setTransformTargetCoordinates,
  setTransformBatchInput,
  setTransformBatchOutput,
  setAccuracyInfo,
  setCoordinateSystemError,
  clearCoordinateSystemError,
} = coordinateSystemSlice.actions;

export default coordinateSystemSlice.reducer;
